import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ResponsivePie } from '@nivo/pie';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { formatDecimal } from '@app/common';
import { BaseLegend } from '../legend/base-legend';
import { PieChartTypeEnum } from '../../../../../models/enums/pie-chart-type-enum';
import { Tooltip } from './tooltip/tooltip';
import { PieChartDataMobxDto } from '../../../../../models/mobx/dtos/statistic/pie-chart-data-mobx-dto';
import { cn } from '../../../../../utils/tailwind';
import { getOpacity } from '../../../../../theme/utils/get-rgb';
import { NoData } from '../../../no-data/no-data';

export interface BasePieChartProps {
    data: PieChartDataMobxDto[];
    type?: PieChartTypeEnum;
    currency?: string | null;
    orientation?: 'horizontal' | 'vertical';
    isFree?: boolean;
    isFullScreen?: boolean;
}

export const BasePieChart: React.FC<BasePieChartProps> = observer((props: BasePieChartProps) => {
    const { data, type, currency, orientation = 'horizontal', isFree = false, isFullScreen } = props;
    const [isLegendScrolledTop, setIsLegendScrolledTop] = useState(true);
    const [isLegendScrolledBottom, setIsLegendScrolledBottom] = useState(true);
    const legendRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const colors = [
        '#3347FF',
        '#FFA733',
        '#865EF8',
        '#FF697B',
        '#15BE96',
        '#8E91AF',
        '#46DEFF',
        '#FFCA85',
        '#AD90FF',
        '#FFB0B9',
        '#72E7CB',
        '#BABDDE',
        '#2930CE',
        '#B37524',
        '#6337E1',
        '#FF3A52',
        '#009B76',
        '#73758E',

        '#484191',
        '#91417B',
        '#6F2B2B',
        '#639641',
        '#459B90',
        '#BE443C',
        '#7B72D5',
        '#BC67A4',
        '#8C4141',
        '#8DBA6F',
        '#77B8B0',
        '#C1625B',
        '#231D61',
        '#7A2E65',
        '#572020',
        '#49742D',
        '#2E7F74',
        '#A82A22',
    ];

    const translated = React.useMemo(() => {
        if (!data.length) return [];

        const items = data.map((d) => ({
            ...d,
            id: i18n.exists(`pages.index.${type}.${d.id.replace(/ /g, '')}`)
                ? t(`pages.index.${type}.${d.id.replace(/ /g, '')}`)
                : d.id,
            name: i18n.exists(`pages.index.${type}.${d.name.replace(/ /g, '')}`)
                ? t(`pages.index.${type}.${d.name.replace(/ /g, '')}`)
                : d.name,
            value: Math.round(d.value * 10) / 10,
        }));

        const sum = items.reduce((acc, row) => acc + row.value, 0);
        const diff = Math.round((100 - sum) * 10) / 10;

        if (diff !== 0) {
            const largest = items.reduce((prev, curr) => (prev.value > curr.value ? prev : curr));
            largest.value = Math.round((largest.value + diff) * 10) / 10;
        }

        return items;
    }, [data, type, t]);

    const handleLegendScroll = () => {
        if (legendRef.current) {
            const { scrollTop } = legendRef.current;
            const { scrollHeight } = legendRef.current;
            const { clientHeight } = legendRef.current;

            if (scrollTop === 0) {
                setIsLegendScrolledTop(true);
            } else {
                setIsLegendScrolledTop(false);
            }

            if (Math.abs(scrollTop + clientHeight - scrollHeight) < 2) {
                setIsLegendScrolledBottom(true);
            } else {
                setIsLegendScrolledBottom(false);
            }
        }
    };

    useEffect(() => {
        if (legendRef.current) {
            const { scrollHeight } = legendRef.current;
            const { clientHeight } = legendRef.current;

            if (scrollHeight > clientHeight) {
                setIsLegendScrolledBottom(false);
            }
        }
    }, []);

    if (!data.some((item) => item.value > 0)) {
        return (
            <NoData compact marginBottom="none">
                {t('layout.tables.noDetailedData')}
            </NoData>
        );
    }

    let layoutClassName = 'flex flex-col';
    if (isFullScreen) {
        layoutClassName =
            'grid h-[calc(100vh_-_10.4rem)] grid-cols-[1fr_1.5fr] max-lg:flex max-lg:flex-col max-lg:gap-[4rem] xxl:h-[calc(100%_-_10rem)] xxl:gap-[10rem] xxl:p-[5rem]';
    } else if (orientation === 'horizontal') {
        layoutClassName = 'grid grid-cols-[1fr_1.5fr]';
    }

    return (
        <div className={cn('h-full gap-vsmall xxl:gap-medium', layoutClassName)}>
            <div
                className={cn(
                    'relative inline-flex w-full pb-[100%] [&_svg]:!overflow-visible',
                    isFullScreen && 'pb-[50%]',
                    isFree && 'blur-[0.3rem]',
                    !isFullScreen && orientation === 'vertical' && 'mx-auto w-[60%] pb-[60%]',
                )}
            >
                <div className="absolute inset-0">
                    <span
                        className={cn(
                            'pointer-events-none absolute top-1/2 left-1/2 w-[30%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-border pb-[30%]',
                            isFullScreen && 'max-lg:w-[15%] max-lg:pb-[15%]',
                        )}
                    />
                    <ResponsivePie
                        theme={{
                            legends: { text: { fontSize: theme.text.size.medium.fontSize } },
                            tooltip: { basic: { fontFamily: theme.base.fontFace } },
                        }}
                        data={translated}
                        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                        innerRadius={0.5}
                        padAngle={1}
                        tooltip={(datum) => {
                            return isFree ? null : <Tooltip type={props.type} currency={currency} data={datum} />;
                        }}
                        cornerRadius={6}
                        activeInnerRadiusOffset={isFree ? 0 : 6}
                        activeOuterRadiusOffset={isFree ? 0 : 6}
                        borderWidth={0}
                        enableArcLabels={false}
                        enableArcLinkLabels={false}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsTextColor={theme.palette.color.gray.main}
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: 'color' }}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                        arcLinkLabel={(value) => `${value.id}`}
                        valueFormat={(value) => `${formatDecimal(value, 1)}%`}
                        colors={colors}
                    />
                </div>
            </div>
            <div className="relative flex-1" style={{ margin: isFullScreen ? 0 : '-2rem -2rem -2rem 0' }}>
                <span
                    className={cn(
                        'pointer-events-none absolute top-0 right-0 left-0 z-10 h-[5rem] transition-all',
                        isLegendScrolledTop ? '-translate-y-[5rem] opacity-0' : 'translate-y-0 opacity-100',
                    )}
                    style={{
                        background: `linear-gradient(0deg, ${getOpacity(
                            theme.palette.background.body,
                            0,
                        )} 0%, ${getOpacity(theme.palette.background.body, 1)} 100%)`,
                    }}
                    aria-hidden
                />
                <span
                    className={cn(
                        'pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-[5rem] transition-all',
                        isLegendScrolledBottom ? 'translate-y-[5rem] opacity-0' : 'translate-y-0 opacity-100',
                    )}
                    style={{
                        background: `linear-gradient(180deg, ${getOpacity(
                            theme.palette.background.body,
                            0,
                        )} 0%, ${getOpacity(theme.palette.background.body, 1)} 100%)`,
                    }}
                    aria-hidden
                />
                <div onScroll={handleLegendScroll} ref={legendRef} className="relative h-full overflow-auto">
                    <div className="absolute top-0 right-[2rem] bottom-0 left-0 flex flex-col items-center justify-center">
                        <BaseLegend
                            data={translated}
                            type={type}
                            colors={colors}
                            isFree={isFree}
                            isFullScreen={isFullScreen}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});
